import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactNode } from 'react';
import Loader from '@/components/Loader.tsx';
import { TypoBody, TypoCallout, TypoCaption, TypoHeading, TypoHeading2, TypoTitle } from '@/components/Typography.tsx';
import PackagesList, { PackageOption } from '@/components/PackagesList.tsx';
import Button from '@/components/Button.tsx';
import VideoPlayer from '@/components/VideoPlayer';
import PACKAGES_QUERY from '@/graphql/queries/purchase-options-for-client.graphql';
import DEFAULT_POSTER from '@/assets/video-thumbnail.png';
import VIDEO_SOURCE from '@/assets/introductory-video.webm';
import { CheckSquare } from '@/components/Icons.tsx';
import { PRICING_PRODUCT_IDS, PricingTable, ProductProps } from '@/pages/Home/Pricing.tsx';
import { TestimonialsCarousel } from '@/pages/Home/Testimonials.tsx';
import useCheckoutAction from '@/hooks/useCheckoutAction.ts';
import { formatPrice } from '@/utils/helpers.tsx';

const Packages = () => {
  const { list, ownedIds, loading, refetch } = usePackages();

  if (loading) return <Loader />;

  // Plans in the comparison table are bought from the table; anything else on offer
  // (e.g. a package unlocked by a promo code) keeps its card below it.
  const otherPackages = list.filter(p => !PRICING_PRODUCT_IDS.has(p.productId));

  // The page sizes itself by its own width (`cq-*` variants), not the viewport's: in the
  // authenticated layout it is at most 40rem wide, and on 640-1060px screens the sidebar
  // squeezes it to as little as ~13rem while the phone (`m:`) styles no longer apply.
  return (
    <div className="@container flex flex-col w-full">
      <TypoHeading2 className={`
        text-neutral-text-strong font-medium px-4 pt-2 pb-3 text-heading2-m
        cq-md:px-10 cq-md:pt-6 cq-md:pb-4 cq-md:text-heading2`}>
        SATPrac Packages
      </TypoHeading2>
      <div className="flex flex-col w-full rounded overflow-hidden">
        <Section tone="dark">
          <TypoTitle className="font-extraBold text-neutral-text-strong text-heading2-m cq-md:text-title-m">
            Ready to win the game?
          </TypoTitle>
          <TypoHeading className="block font-semibold text-neutral-text mt-4 text-heading-m cq-md:text-heading">
            Don't just take our word for it!
          </TypoHeading>
          <TypoBody>Hear what SATPrac COMPLETE members are saying.</TypoBody>
          <TestimonialsCarousel className="mt-8" />
        </Section>

        {/* Narrower side padding than the other bands: a full-width column (40rem) then
            leaves the 37.5rem the compact table needs to show all four plans. */}
        <Section tone="light" className="text-center cq-md:!px-5">
          <TypoTitle className="font-extraBold text-heading2-m cq-md:text-title-m">
            Choose what's right for you
          </TypoTitle>
          <PricingTable
            compact
            className="mt-8 mb-8 cq-md:mt-12"
            renderFooter={(product) => <PlanFooter product={product} list={list} ownedIds={ownedIds} />}
          />
          <PromoCodeForm onSubmit={refetch} />
        </Section>

        {otherPackages.length > 0 && (
          <Section tone="dark" className="!px-2">
            <PackagesList title="Other packages" list={otherPackages} />
          </Section>
        )}

        <Section tone="dark">
          <TypoHeading className="block font-semibold text-neutral-text-strong text-heading-m cq-md:text-heading">
            Not sure how it works?
          </TypoHeading>
          <TypoBody className="mb-6">
            In this video I walk you through what's on SATPrac and what's accessible with each package.
          </TypoBody>
          <VideoPlayer src={VIDEO_SOURCE} poster={DEFAULT_POSTER} className="w-full" />
        </Section>

        <Section tone="light">
          <TypoHeading className="block font-semibold text-heading-m cq-md:text-heading">
            Why is SATPrac the right site for your test preparation needs?
          </TypoHeading>
          <TypoBody className="mt-2">
            The digital SAT is a game of skill and you win games of skill quite simply by practicing, practicing,
            and then practicing a little more. Take a test, see what you get wrong, see why you got it wrong,
            see how to get it right next time, review that information, and then repeat the process.
            That is how SATPrac is put together. It is a well-oiled machine that will put you on a path to victory.
            In the short video above, I walk you through how the site is laid out and how you can use it
            to put yourself in position to dominate the SAT!
          </TypoBody>
        </Section>
      </div>
    </div>
  );
};

// Alternating dark and light bands break up the page, as in the proposed design.
interface SectionProps {
  tone: 'dark' | 'light';
  className?: string;
  children: ReactNode;
}

const Section = ({ tone, className = '', children }: SectionProps) => {
  const toneClasses = tone === 'dark'
    ? 'bg-neutral2 text-neutral2-text-soft'
    : 'bg-common-white text-[#151012]';

  return <section className={`px-4 py-8 cq-md:px-10 cq-md:py-12 ${toneClasses} ${className}`}>{children}</section>;
};

interface PlanFooterProps {
  product: ProductProps;
  list: PackageOption[];
  ownedIds: Set<number>;
}

const PlanFooter = ({ product, list, ownedIds }: PlanFooterProps) => {
  if (ownedIds.has(product.productId)) {
    return <PlanPrice className="text-success-neutral-text-softer">Owned</PlanPrice>;
  }

  const option = list.find(p => p.productId === product.productId);

  if (!option?.price) return <PlanPrice className="text-[#00000080]">Unavailable</PlanPrice>;
  if (!option.price.price) return <PlanPrice>Free</PlanPrice>;

  return <BuyPlan productPriceId={option.price.productPriceId} price={option.price.price} />;
};

const BuyPlan = ({ productPriceId, price }: { productPriceId: number; price: number }) => {
  const { checkout, loading } = useCheckoutAction(productPriceId);

  return (
    <>
      <PlanPrice>{formatPrice(price)}</PlanPrice>
      <Button onClick={() => checkout()} disabled={loading} className="w-full max-w-36 !px-2">
        Get Access
      </Button>
    </>
  );
};

const PlanPrice = ({ className = 'text-[#151012]', children }: { className?: string; children: ReactNode }) => (
  <TypoCallout className={`font-bold italic ${className}`}>{children}</TypoCallout>
);

const PromoCodeForm = ({ onSubmit }: { onSubmit: UsePackagesOutput['refetch'] }) => {
  const [loading, setLoading] = useState(false);
  const [lastSubmittedCode, setLastSubmittedCode] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    watch,
  } = useForm({ defaultValues: { code: '' }, resolver: yupResolver(PromoCodeSchema) });

  const submitCallback = useCallback(async ({ code = '' }) => {
    setLoading(true);
    await onSubmit({ code });
    setLoading(false);
    setLastSubmittedCode(code);
  }, [onSubmit]);

  const code = watch('code');

  // One inline row under the table, right-aligned to its edge; in a narrow column the label
  // wraps above the input. `TextField` always stacks its label above a dark input, so this
  // row uses a plain light input instead to sit on the white section.
  return (
    <form
      autoComplete="off"
      noValidate
      onSubmit={handleSubmit(submitCallback)}
      className="flex flex-col items-stretch gap-1 w-full cq-md:items-end"
    >
      <div className="flex flex-wrap items-center gap-3 cq-md:flex-nowrap">
        <label htmlFor="promo-code" className="basis-full text-callout font-semibold text-[#151012] cq-md:basis-auto">
          Have a promo code?
        </label>
        <input
          {...register('code')}
          id="promo-code"
          type="text"
          placeholder="#SK123"
          aria-invalid={Boolean(errors?.code)}
          className={`cq-md:w-40 rounded-xs border px-3 py-2 text-callout text-[#151012] bg-common-white outline-none
            placeholder:text-[#00000066] focus:border-info flex-1 min-w-0 cq-md:flex-none
            ${errors?.code ? 'border-danger' : 'border-[#00000026]'}`}
          onFocus={(event) => event.target.select()}
        />
        <Button className="!py-2 min-w-24" color="info" type="submit" loading={loading}>
          { isSubmitSuccessful && lastSubmittedCode == code
            ? <><CheckSquare width={20} height={20} /> Applied</>
            : <>Apply</> }
        </Button>
      </div>
      {errors?.code?.message && (
        <TypoCaption className="text-danger first-letter:uppercase">{errors.code.message}</TypoCaption>
      )}
    </form>
  );
};

const usePackages = (): UsePackagesOutput => {
  const { data, loading, refetch } = useQuery<PackagesQueryResult>(PACKAGES_QUERY);

  return useMemo(() => ({
    loading,
    refetch,
    ownedIds: new Set((data?.purchasedProducts ?? []).map(p => p.productId)),
    list: (data?.purchaseOptionsForClient ?? [])
      // We filter out any packages that don't have a price, as they are supposed to appear only with promo code
      // See: https://incept.slack.com/archives/C05JFSSN7PY/p1725035841042909
      .filter(p => p?.price?.productPriceId)
      .map(p => ({
        ...p,
        description: JSON.parse(p.description as any),
      }))
      .sort((a, b) => (a.price?.price || 0) - (b.price?.price || 0))
  }), [data?.purchaseOptionsForClient, data?.purchasedProducts, loading, refetch]);
};

const PromoCodeSchema = yup.object().shape({
  code: yup.string().max(45),
});

interface PackagesQueryResult {
  purchaseOptionsForClient: PackageOption[];
  purchasedProducts: Array<{ productId: number }>;
}

interface UsePackagesOutput {
  loading: boolean;
  list: PackageOption[];
  ownedIds: Set<number>;
  refetch: (variables?: { code: string }) => Promise<any>;
}

export default Packages;
